/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import { format } from 'date-fns';
import { RootState } from '../../../redux/store';
import { CircPieChart, ChartLegend } from '../../metricsChart';
import { setComplianceReportFilter } from '../../../redux/actions';
import { logReportsClick } from '../../../utils/analyticsHelpers';
import {
  calculateTotalCompliance,
  reduceComplianceByUnit
} from '../../../utils/complianceFunctions';

/**
 * Global Type Definition Imports
 */
import {
  ComplianceObj,
  ReducedUnitComp,
  ComplianceReport as IComplianceReport,
  Unit,
  Filter
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  ComplianceMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface ComplianceMetricChartProps {
  complianceReport: ComplianceObj[];
  selectedUnits: { id: string; unit: string }[];
  patientSummaryReportFilter: Filter;
  units: Unit[];
}

const ComplianceMetricChart: FC<ComplianceMetricChartProps> = ({
  complianceReport = [],
  selectedUnits,
  patientSummaryReportFilter,
  units = []
}) => {
  const dispatch = useDispatch();
  const { '*': view } = useParams();
  const navigateTo = useNavigate();
  const { logTrackingEvent } = useAnalyticsApi();

  const [listToDisplayTotal, setListToDisplayTotal] =
    useState<IComplianceReport | null>(null);

  useEffect(() => {
    if (units.length > 0) {
      const reducedCompliance = reduceComplianceByUnit(complianceReport, units);
      const complianceValues: ReducedUnitComp[] =
        Object.values(reducedCompliance);
      const complianceByUnitFilter = complianceValues.filter(
        (comp: ReducedUnitComp) =>
          // if no units are selected, default to entire facility to match the rest of the widgets
          selectedUnits.length > 0
            ? selectedUnits.some(unit => unit.id === comp.groupId)
            : comp
      );

      const cleanCompliance = complianceByUnitFilter.map(
        (item: ReducedUnitComp) => {
          return {
            unit: item.groupName,
            unitId: item.groupId,
            unitType: item.locTypeDisplay,
            census: item.combinedData.census,
            roundedPercent: item.combinedData.roundedPct,
            roundedNumber: item.combinedData.roundedTotal,
            unablePercent: item.combinedData.unablePct,
            unableNumber: item.combinedData.unable,
            notRoundedPercent: item.combinedData.nonePct,
            notRoundedNumber: item.combinedData.none
          };
        }
      );

      setListToDisplayTotal(calculateTotalCompliance(cleanCompliance, '', ''));
    }
  }, [complianceReport, selectedUnits, units]);

  const data = useMemo(() => {
    return [
      listToDisplayTotal?.roundedNumber ?? 0,
      listToDisplayTotal?.notRoundedNumber ?? 0
    ];
  }, [listToDisplayTotal]);
  const chartLegendLabels = useMemo(() => {
    return {
      completeCount: listToDisplayTotal?.roundedNumber ?? 0,
      completePercent: listToDisplayTotal?.roundedPercent ?? 0,
      todoCount: listToDisplayTotal?.notRoundedNumber ?? 0,
      todoPercent: listToDisplayTotal?.notRoundedPercent ?? 0
    };
  }, [listToDisplayTotal]);

  const openComplianceReportTable = () => {
    dispatch(
      setComplianceReportFilter({
        ...patientSummaryReportFilter,
        startDate:
          patientSummaryReportFilter.startDate ??
          format(new Date(), 'yyyy-MM-dd')
      })
    );
    if (view)
      logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'compliance'));
    setTimeout(() => {
      navigateTo('/reports/patient/compliance', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <ComplianceMetricContainer
      id="Summary-Compliance-Metric-Chart"
      onClick={openComplianceReportTable}
    >
      <TitleContainer style={{ paddingLeft: '1.75rem' }}>
        Compliance
      </TitleContainer>
      <div className="d-flex flex-column px-0 mx-0">
        {/* dont draw the census metrics chart if no data */}
        {data.length > 0 && <CircPieChart data={data} />}
        {data.length > 0 && (
          <ChartLegend
            completeCount={chartLegendLabels.completeCount}
            completePercent={chartLegendLabels.completePercent}
            todoCount={chartLegendLabels.todoCount}
            todoPercent={chartLegendLabels.todoPercent}
          />
        )}
      </div>
    </ComplianceMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    complianceReport: state.ReportReducer.complianceReport,
    selectedUnits: state.ConfigReducer.selectedUnits,
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(ComplianceMetricChart);
